import { defineStore } from 'pinia'
import * as Sentry from '@sentry/vue'

import { STORAGE_KEYS } from '@/constants/storage'
import type { Role, UserProfile } from '@/types'
import { fetchMe, loginRequest, logoutRequest, type LoginPayload } from '../api'
import { useStudioStore } from '@/modules/studio/stores/studio.store'
import { useAppContextStore } from '@/stores/app-context.store'
import { useSubscriptionStore } from '@/stores/subscription.store'
import pinia from '@/stores/pinia'
import { destroyEcho } from '@/services/echo'

interface AuthState {
  token: string | null
  user: UserProfile | null
  status: 'idle' | 'loading' | 'authenticated' | 'error'
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    token: localStorage.getItem(STORAGE_KEYS.token),
    user: null,
    status: 'idle',
  }),
  getters: {
    isAuthenticated: (state) => Boolean(state.token),
    isSuperAdmin: (state): boolean => state.user?.is_super_admin ?? false,
    role: (state): Role | null => state.user?.role ?? null,
    userStudios: (state) => state.user?.studios ?? [],
  },
  actions: {
    async login(payload: LoginPayload) {
      this.status = 'loading'
      try {
        const { token } = await loginRequest(payload)
        this.token = token
        localStorage.setItem(STORAGE_KEYS.token, token)
        await this.loadProfile()
        this.status = 'authenticated'
      } catch (error) {
        this.status = 'error'
        throw error
      }
    },
    async loadProfile() {
      const profile = await fetchMe()
      this.user = profile
      Sentry.setUser({ id: String(profile.id), email: profile.email, username: profile.name })
      const studioStore = useStudioStore(pinia)
      studioStore.initializeFromProfile(profile.studios)
    },
    async logout(options?: { skipRequest?: boolean }) {
      if (!options?.skipRequest) {
        try {
          await logoutRequest()
        } catch {
          // ignore network errors during logout
        }
      }
      this.token = null
      this.user = null
      this.status = 'idle'
      localStorage.removeItem(STORAGE_KEYS.token)
      Sentry.setUser(null)
      const studioStore = useStudioStore(pinia)
      studioStore.clear()
      const contextStore = useAppContextStore(pinia)
      contextStore.reset()
      const subscriptionStore = useSubscriptionStore(pinia)
      subscriptionStore.clearSubscriptionState()
      destroyEcho()
    },
  },
})
